import React, { useEffect, useState } from "react";
import { supabase, fmtBRL } from "@/lib/supabase";
import { db } from "@/lib/db";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { TrendingUp, Users, Briefcase, FileText, Wallet } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const Metric = ({ label, value, sub, Icon, accent, testid }) => (
  <Card className="p-6 bg-white border border-black/10 rounded-none" data-testid={testid}>
    <div className="flex items-start justify-between mb-4">
      <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A]">{label}</div>
      <Icon className={`w-4 h-4 ${accent ? "text-[#D9381E]" : "text-[#8A8A8A]"}`} />
    </div>
    <div className="font-serif text-4xl">{value}</div>
    {sub && <div className="text-sm text-[#4A4A4A] mt-1">{sub}</div>}
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [m, setM] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [leadsData, projetosData, financeiroData, propostasData] = await Promise.all([
          db.select("leads").catch(() => []),
          db.select("projetos").catch(() => []),
          db.select("financeiro").catch(() => []),
          db.select("propostas").catch(() => []),
        ]);

        const leads = Array.isArray(leadsData) ? leadsData : [];
        const projetos = Array.isArray(projetosData) ? projetosData : [];
        const financeiro = Array.isArray(financeiroData) ? financeiroData : [];
        const propostas = Array.isArray(propostasData) ? propostasData : [];

        const now = new Date();
        const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
        const entradasPagas = financeiro.filter((f) => f && f.tipo === "entrada" && f.status === "pago");
        const totalReceita = entradasPagas.reduce((s, f) => s + Number(f.valor || 0), 0);
        const receitaMes = entradasPagas
          .filter((f) => (f.created_at || "").startsWith(monthKey))
          .reduce((s, f) => s + Number(f.valor || 0), 0);
        const aReceber = financeiro
          .filter((f) => f && f.tipo === "entrada" && f.status === "pendente")
          .reduce((s, f) => s + Number(f.valor || 0), 0);
        const ticketMedio = entradasPagas.length ? totalReceita / entradasPagas.length : 0;

        const fechados = leads.filter((l) => l && l.status === "fechado").length;
        const leadsAbertos = leads.filter((l) => l && !["fechado", "perdido"].includes(l.status)).length;
        const conversao = leads.length ? (fechados / leads.length) * 100 : 0;
        const projetosAtivos = projetos.filter((p) => p && p.status === "em andamento").length;

        const chart = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
          const label = d.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
          const total = entradasPagas
            .filter((f) => (f.created_at || "").startsWith(key))
            .reduce((s, f) => s + Number(f.valor || 0), 0);
          chart.push({ month: label, revenue: Math.round(total * 100) / 100 });
        }

        setM({
          receitaMes, totalReceita, ticketMedio, aReceber, leadsAbertos,
          conversao: Math.round(conversao * 10) / 10, projetosAtivos,
          totalPropostas: propostas.length, totalLeads: leads.length, chart,
        });
      } catch (err) {
        console.error("Erro dashboard:", err);
      }
    })();
  }, [user]);

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-2">Painel</div>
          <h1 className="font-serif text-4xl md:text-5xl">Olá, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-[#4A4A4A] mt-2">Aqui está sua revisão em números.</p>
        </div>
        
        {user?.plan && (
          <div className="bg-[#F6F5F0] border border-black/10 p-4 flex items-center justify-between gap-6">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-tighter text-[#8A8A8A]">Plano Atual</div>
              <div className="font-serif text-lg capitalize">{user.plan}</div>
            </div>
            <a 
              href="https://www.mercadopago.com.br/subscriptions" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs font-mono uppercase tracking-widest text-[#D9381E] hover:underline"
            >
              Gerenciar Assinatura
            </a>
          </div>
        )}
      </div>

      {!m ? (
        <div className="text-[#8A8A8A] font-mono text-sm">Carregando métricas…</div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Metric label="Receita do mês" value={fmtBRL(m.receitaMes)} sub={`Total: ${fmtBRL(m.totalReceita)}`} Icon={TrendingUp} accent testid="metric-monthly-revenue" />
            <Metric label="Ticket médio" value={fmtBRL(m.ticketMedio)} Icon={Wallet} testid="metric-avg-ticket" />
            <Metric label="A receber" value={fmtBRL(m.aReceber)} Icon={Wallet} testid="metric-pending" />
            <Metric label="Taxa de conversão" value={`${m.conversao}%`} sub={`${m.leadsAbertos} leads abertos`} Icon={Users} testid="metric-conversion" />
          </div>
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <Metric label="Projetos ativos" value={m.projetosAtivos} Icon={Briefcase} testid="metric-projects" />
            <Metric label="Propostas criadas" value={m.totalPropostas} Icon={FileText} testid="metric-proposals" />
            <Metric label="Leads" value={m.totalLeads} Icon={Users} testid="metric-leads" />
          </div>
          <Card className="p-6 bg-white border border-black/10 rounded-none">
            <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-4">Receita últimos 6 meses</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={m.chart}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#E5E3DB" />
                  <XAxis dataKey="month" stroke="#4A4A4A" fontSize={12} />
                  <YAxis stroke="#4A4A4A" fontSize={12} />
                  <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={{ fontFamily: "Manrope", borderRadius: 0 }} />
                  <Bar dataKey="revenue" fill="#D9381E" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
